import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Linkedin, Phone, CheckCircle2 } from "lucide-react";
import { SectionLabel, HighlightText, SectionHeading } from "@/components/Bits";
import { CALENDLY_URL, PHONE_DISPLAY } from "@/lib/api";

const LEADERS = [
  {
    initials: "AD", name: "Ankita Dewani", role: "Co-Founder & Chief Business Officer", linkedin: "linkedin.com/in/ankitadewani", dark: true,
    image: "/teamImages/Ankita-image.png",
    quote: "Most businesses don't have a talent problem. They have a clarity problem. Once you know where to plug in the right resource, everything falls into place.",
    bio: "16 years of experience. Three brands led. One belief that never changed that the right resource in the right place changes everything. At Aidify, Ankita helps clients go beyond \"we need someone\" to understanding exactly what they need and why.",
    pills: ["16+ Years", "3 Brands Led", "Co-Founder"],
  },
  {
    initials: "SG", name: "Sushant Gupta", role: "Co-Founder & CEO", linkedin: "linkedin.com/in/theofficialsushantgupta/",
    image: "https://customer-assets.emergentagent.com/job_design-to-live-15/artifacts/xj0phsvh_Sushant_Gupta.png",
    quote: "Businesses don't need remote help. They need dependable people backed by structure, supervision, and accountability. That is exactly what we build.",
    bio: "With 16 years in global talent, Sushant founded Aidify to provide the structure brilliant Indian professionals deserve. A former pilot, he swapped the cockpit for the company, applying aviation's discipline to scaling businesses.",
    pills: ["Operations", "Scale Systems", "Co-Founder"],
  },
  {
    initials: "SL", name: "Sweety L.", role: "Business Head", linkedin: "linkedin.com/in/sweety-l", dark: true,
    image: "/teamImages/supriya-image.png",
    quote: "Every client has a different version of success. My job is to make sure we find it together and deliver it without them having to ask twice.",
    bio: "Sweety has been the face of Aidify since day one. With 10 years of experience and a presence clients naturally gravitate towards, she connects the dots between clients and teams, expectations and delivery, what was promised and what actually lands.",
    pills: ["Training", "Delivery Ops", "Performance"],
  },
];

const ACCOUNTABLE = [
  { num: "01", title: "Who You Meet", italic: "They Know Your Market", desc: "Every US partner is based in America and brings real on-the-ground knowledge of US business culture, communication standards, and client expectations." },
  { num: "02", title: "They are Fully Accountable", desc: "When you have a concern, your US partner is the first call. Not a support ticket. Not a chatbot. A real person with full responsibility for your placement." },
  { num: "03", title: "They Stay Involved", desc: "Our US partners do not disappear after day one. They monitor performance, check in regularly, and make sure your professional keeps delivering." },
];

const NEVER_COMPROMISE = [
  { n: "01", t: "Accountability First", d: "We believe accountability is not a feature. It is a foundation. Every decision we make is built around making sure someone is always answerable for the work we do." },
  { n: "02", t: "Talent Without Borders", d: "Where someone is based should never limit what they can deliver. We find the best people wherever they are and give them the platform to do their best work." },
  { n: "03", t: "Honest Relationships", d: "We do not overpromise and underdeliver. We tell you what we can do, we do it, and we are honest when something needs to change." },
  { n: "04", t: "Built to Grow With You", d: "We are not here for a one-time placement. We are here to be the talent partner your business relies on as it grows, evolves, and scales into new functions." },
];

const CULTURE = [
  { n: "01", t: "Remote First. Always.", d: "Our entire model is built around remote work — so we practice what we preach. Flexibility is not a perk here. It is how we operate." },
  { n: "02", t: "Growth That is Real", d: "We invest in our people the same way we invest in the professionals we place. Training, tools, and real opportunities to grow your role as the company grows." },
  { n: "03", t: "Work That Matters", d: "Every placement we make changes someone's business. Every professional we put on our bench gets an opportunity they might not have had otherwise." },
];

export default function OurTeam() {
  return (
    <>
      {/* HERO */}
      <section className="max-w-[1320px] mx-auto px-5 lg:px-10 pt-10 lg:pt-16">
        <SectionLabel>Our team</SectionLabel>
        <div className="mt-8" data-reveal>
          <h1 className="font-display font-extrabold tracking-tight text-forest-600 text-5xl md:text-7xl lg:text-[96px] leading-[0.95]">
            We are not just a platform.
          </h1>
          <h2 className="mt-4 font-serif-italic text-forest-600 text-4xl md:text-6xl lg:text-[72px] leading-[1.05]">
            <HighlightText>We are a team that</HighlightText> genuinely cares.
          </h2>
          <p className="mt-10 max-w-3xl text-forest-500 text-lg md:text-xl leading-relaxed">
            Aidify was built by people who have felt the frustration of bad hires, unreliable freelancers, and offshore solutions with nobody accountable. We built the company we wished existed. And we show up every day to make sure it stays that way.
          </p>
        </div>
      </section>

      {/* HOW WE STARTED */}
      <section className="max-w-[1320px] mx-auto px-5 lg:px-10 mt-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5" data-reveal>
            <SectionLabel>How we started</SectionLabel>
            <h2 className="mt-7 font-display font-extrabold text-4xl md:text-5xl lg:text-6xl text-forest-600 leading-[1.05]">
              Built out of frustration.
            </h2>
            <h3 className="mt-2 font-serif-italic text-forest-600 text-3xl md:text-4xl lg:text-5xl leading-tight">
              Grown out of purpose.
            </h3>
            <div className="flex flex-wrap gap-3 mt-10">
              <div className="aidify-card px-5 py-4 flex flex-col">
                <span className="font-display font-extrabold text-2xl text-forest-600">2 hrs</span>
                <span className="text-[10px] uppercase tracking-[0.18em] text-forest-400/70 mt-0.5">to place</span>
              </div>
              <div className="aidify-card-dark px-5 py-4 flex flex-col">
                <span className="font-display font-extrabold text-2xl text-cream-100">100%</span>
                <span className="text-[10px] uppercase tracking-[0.18em] text-cream-100/60 mt-0.5">US Oversight</span>
              </div>
              <div className="aidify-card-sage px-5 py-4 flex flex-col">
                <span className="font-display font-extrabold text-2xl text-forest-600">200+</span>
                <span className="text-[10px] uppercase tracking-[0.18em] text-forest-500/70 mt-0.5">on bench</span>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7 text-forest-500 text-[17px] leading-[1.7] space-y-6" data-reveal>
            <p>
              Every business deserves access to great talent without the cost, complexity, and risk that usually comes with it. That was the idea Aidify was built on.
            </p>
            <p>
              We saw businesses struggling to scale because hiring was too slow, too expensive, or too unreliable. We saw offshore solutions that promised the world and delivered inconsistency. And we saw a gap that nobody was filling properly, global talent with real local accountability.
            </p>
            <p>
              So we built it ourselves. An operations team across India and multiple global offices. A network of US business partners who take full local responsibility for every placement. And a bench of pre-vetted professionals ready to plug into any business within hours.
            </p>
            <div className="border-t border-forest-500/15 pt-6">
              <p className="font-serif-italic text-forest-600 text-xl md:text-2xl">
                Aidify is not just a staffing solution. It is a new way of building a team.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* LEADERSHIP */}
      <section className="max-w-[1320px] mx-auto px-5 lg:px-10 mt-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12">
          <div className="lg:col-span-7" data-reveal>
            <SectionLabel>Leadership team</SectionLabel>
            <h2 className="mt-6 font-display font-extrabold text-5xl md:text-6xl lg:text-7xl text-forest-600 leading-[1.0]">
              The people who made
            </h2>
            <h3 className="mt-2 font-serif-italic text-forest-600 text-4xl md:text-5xl lg:text-6xl leading-tight">
              <HighlightText>This possible.</HighlightText>
            </h3>
          </div>
          <div className="lg:col-span-5 lg:pt-12" data-reveal>
            <p className="text-forest-500 text-base leading-relaxed">
              A small team with a big mission. Every person here has a role that matters and a name your clients can put to it.
            </p>
          </div>
        </div>

        <div className="space-y-5">
          {LEADERS.map((p, i) => (
            <div key={i} className={`p-6 lg:p-8 ${p.dark ? "aidify-card-dark" : "aidify-card"}`} data-reveal>
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
                {/* Image / Initials block */}
                <div className={`lg:col-span-4 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                  <div className="aidify-card-sage relative h-full min-h-[320px] flex items-center justify-center p-6 overflow-hidden">
                    <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 text-[11px] font-medium text-forest-600 bg-forest-600/10 px-3 py-1.5 rounded-full z-10">
                      <span className="w-1.5 h-1.5 rounded-full bg-forest-600" />
                      Aidify team - {String(i + 1).padStart(2, "0")}
                    </span>
                    {p.image ? (
                      <img
                        src={p.image}
                        alt={p.name}
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                    ) : (
                      <span className="font-display font-extrabold text-[8rem] leading-none text-forest-600">{p.initials}</span>
                    )}
                    <a
                      href={`https://${p.linkedin}`}
                      target="_blank"
                      rel="noreferrer"
                      className="absolute bottom-4 right-4 inline-flex items-center gap-1.5 text-xs font-medium bg-forest-600 text-cream-100 px-3 py-1.5 rounded-full hover:bg-forest-500 transition-colors z-10"
                    >
                      <Linkedin size={12} /> LinkedIn
                    </a>
                  </div>
                </div>

                {/* Content */}
                <div className={`lg:col-span-8 flex flex-col ${i % 2 === 1 ? "lg:order-1" : ""}`}>
                  <div className={`text-sm font-serif-italic ${p.dark ? "text-cream-100/60" : "text-forest-400/70"}`}>
                    -{String(i + 1).padStart(2, "0")}/{String(LEADERS.length).padStart(2, "0")}
                  </div>
                  <h3 className={`mt-3 font-display font-extrabold text-4xl md:text-5xl ${p.dark ? "text-cream-100" : "text-forest-600"}`}>
                    {p.name.split(" ")[0]}{" "}
                    <span className="font-serif-italic font-normal">{p.name.split(" ").slice(1).join(" ")}</span>
                  </h3>
                  <div className={`text-sm mt-2 ${p.dark ? "text-cream-100/70" : "text-forest-500"}`}>{p.role}</div>

                  <div className={`mt-6 pl-5 border-l-2 ${p.dark ? "border-sage-200/50" : "border-sage-300"}`}>
                    <p className={`font-serif-italic text-lg md:text-xl leading-relaxed ${p.dark ? "text-cream-100/90" : "text-forest-600"}`}>
                      "{p.quote}"
                    </p>
                  </div>

                  <p className={`mt-6 text-[15px] leading-[1.7] ${p.dark ? "text-cream-100/75" : "text-forest-400"}`}>{p.bio}</p>

                  <div className="mt-auto pt-6 flex flex-wrap gap-2">
                    {p.pills.map((pill, j) => (
                      <span
                        key={j}
                        className={`px-4 py-1.5 rounded-full text-xs font-medium ${
                          p.dark
                            ? "bg-forest-500/40 text-cream-100/90 border border-cream-100/15"
                            : "bg-sage-200/70 text-forest-600 border border-forest-500/10"
                        }`}
                      >
                        • {pill.toUpperCase()}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      

      {/* ACCOUNTABLE */}
      <section className="max-w-[1320px] mx-auto px-5 lg:px-10 mt-24" data-reveal>
        <div className="aidify-card-dark p-8 md:p-12">
          <div className="flex items-end justify-between flex-wrap gap-6">
            <div>
              <span className="section-pill !bg-forest-500 !text-cream-100/80 !border-forest-400">Local accountability</span>
              <h2 className="mt-5 font-display font-extrabold text-3xl md:text-5xl text-cream-100 leading-tight">
                The people accountable
                <br />
                <span className="font-serif-italic font-normal text-sage-200">in your market.</span>
              </h2>
            </div>
            <a href={CALENDLY_URL} target="_blank" rel="noreferrer" className="btn-on-dark">Book a Discovery Call <ArrowRight size={16} /></a>
          </div>
          <p className="mt-6 text-cream-100/70 max-w-2xl">
            Our US partners are not just figureheads. They are experienced business professionals who live in your market, understand your standards, and put their name behind every placement.
          </p>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            {ACCOUNTABLE.map((a, i) => (
              <div key={i} className="bg-forest-500/40 rounded-xl p-5">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs uppercase tracking-[0.2em] text-cream-100/60">{a.num}</span>
                </div>
                <h4 className="font-display font-bold text-cream-100 text-lg">{a.title}</h4>
                {a.italic && <p className="font-serif-italic text-sage-200 mt-1">{a.italic}</p>}
                <p className="text-cream-100/75 text-sm mt-3 leading-relaxed">{a.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* BENCH */}
      <section className="max-w-[1320px] mx-auto px-5 lg:px-10 mt-24">
        <SectionHeading
          label="Beyond leadership"
          title="A small leadership team."
          italic="A very large bench."
          description="Behind our leadership is a growing network of professionals, US partners, and operations staff spread across multiple countries working every day to deliver on our promise."
        />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { v: "200+", l: "Pre-vetted professionals on bench" },
            { v: "30+", l: "US business partners across America", dark: true },
            { v: "60+", l: "Operations staff across global offices" },
            { v: "4", l: "Core service verticals" },
          ].map((s, i) => (
            <div key={i} className={`p-6 ${s.dark ? "aidify-card-dark" : "aidify-card"}`} data-reveal>
              <div className={`font-display font-extrabold text-5xl ${s.dark ? "text-cream-100" : "text-forest-600"}`}>{s.v}</div>
              <div className={`mt-3 text-xs uppercase tracking-[0.18em] ${s.dark ? "text-cream-100/60" : "text-forest-400/70"}`}>{s.l}</div>
            </div>
          ))}
        </div>
      </section>
      

      {/* NEVER COMPROMISE */}
      <section className="max-w-[1320px] mx-auto px-5 lg:px-10 mt-24">
        <SectionHeading
          label="What we stand for"
          title="The things we will never"
          italic="compromise on."
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {NEVER_COMPROMISE.map((c, i) => (
            <div key={i} className="aidify-card p-7" data-reveal>
              <div className="text-[11px] uppercase tracking-[0.2em] text-forest-400/70">{c.n}</div>
              <h4 className="mt-2 font-display font-bold text-2xl text-forest-600">{c.t}</h4>
              <p className="mt-3 text-sm text-forest-400 leading-relaxed">{c.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CULTURE */}
      <section className="max-w-[1320px] mx-auto px-5 lg:px-10 mt-24" data-reveal>
        <div className="aidify-card-sage p-8 md:p-12">
          <SectionHeading
            label="Life at aidify"
            title="A place where great work"
            italic="gets the recognition it deserves."
            description="We are a remote-first, globally distributed team that believes in flexibility, growth, and making work feel meaningful."
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {CULTURE.map((c, i) => (
              <div key={i} className="bg-cream-100/60 rounded-2xl p-6">
                <div className="text-[11px] uppercase tracking-[0.2em] text-forest-500/70">{c.n}</div>
                <h4 className="mt-2 font-display font-bold text-lg text-forest-600">{c.t}</h4>
                <p className="mt-2 text-sm text-forest-500 leading-relaxed">{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MEET CTA */}
      <section className="max-w-[1320px] mx-auto px-5 lg:px-10 mt-24" data-reveal>
        <div className="aidify-card-dark p-8 md:p-12">
          <span className="section-pill !bg-forest-500 !text-cream-100/80 !border-forest-400">Want to meet us</span>
          <h3 className="mt-5 font-display font-extrabold text-4xl md:text-6xl text-cream-100 leading-tight">
            Book a Call.
            <br />
            <span className="font-serif-italic font-normal text-sage-200">Talk to a Real Person.</span>
          </h3>
          <p className="mt-6 text-cream-100/70 max-w-xl">
            We don't outsource the first conversation. You'll talk to one of us and figure out the fit, the brief, and what good looks like.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <a href={CALENDLY_URL} target="_blank" rel="noreferrer" className="btn-on-dark">Book a Discovery Call <ArrowRight size={16} /></a>
            <a href="tel:+16465951666" className="text-cream-100/70 text-sm hover:text-cream-100 inline-flex items-center gap-2"><Phone size={14}/> or call <span className="underline">{PHONE_DISPLAY}</span></a>
          </div>
        </div>
      </section>
    </>
  );
}
